<template>
    <div class="col-md-8 mx-auto">
        <div class="loader" v-if="loading"></div>
        <div v-else>
            <page-title :title="$t('accommodation.accommodationPlaces')" :showTitle="false"></page-title>

            <div class="btn-group" role="group" aria-label="Basic example">
                <button type="button"
                        class="btn btn-primary"
                        @click="backToRegister">
                    <i class="fas fa-th-list"></i> {{ $t('backToList') }}
                </button>
            </div>

            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{$t('accommodation.title')}}</div>
                </div>
                <div class="card-body row">
                    <div class="form-group col-md-6">
                        <label :for="'name' + _uid" class="required font-weight-bold">{{ $t('accommodation.name') }}</label>
                        <input class="form-control"
                               :id="'name' + _uid"
                               :name="'name' + _uid"
                               v-validate="isDifferentName? 'required|unique:/api/AccommodationPlace/IsNameUnique': 'required'"
                               :data-vv-as="$t('accommodation.name')"
                               v-model="accommodationPlace.name" />
                        <span class="text-danger" v-if="errors.has('name' + _uid)">{{ errors.first('name' + _uid) }}</span>
                    </div>
                    <div class="form-group col-md-6">
                        <label :for="'nameEN' + _uid" class="font-weight-bold">{{ $t('accommodation.nameEN') }}</label>
                        <input class="form-control"
                               :id="'nameEN' + _uid"
                               :name="'nameEN' + _uid"
                               :data-vv-as="$t('accommodation.nameEN')"
                               v-model="accommodationPlace.nameEn" />
                        <span class="text-danger" v-if="errors.has('nameEN' + _uid)">{{ errors.first('nameEN' + _uid) }}</span>
                    </div>
                    <div class="form-group col-md-6">
                        <label :for="'type' + _uid" class="font-weight-bold">{{ $t('accommodation.type') }}</label>
                        <dropdown v-model="accommodationPlace.typeId"
                                  :load-by-parent-sort="true"
                                  :parent-sort="nomenclature.AccommodationPlaceType"
                                  :fieldName="$t('accommodation.type')"></dropdown>
                    </div>
                    <div class="form-group col-md-6">
                        <label :for="'category' + _uid" class="font-weight-bold">{{ $t('accommodation.category') }}</label>
                        <dropdown v-model="accommodationPlace.categoryId"
                                  :load-by-parent-sort="true"
                                  :parent-sort="nomenclature.AccommodationPlaceCategory"
                                  :fieldName="$t('accommodation.category')"></dropdown> <!-- TODO: -->
                    </div>
                    <div class="form-group col-md-12">
                        <label :for="'url' + _uid" class="font-weight-bold">{{ $t('accommodation.url') }}</label>
                        <input class="form-control"
                               :id="'url' + _uid"
                               :name="'url' + _uid"
                               :data-vv-as="$t('accommodation.url')"
                               v-model="accommodationPlace.url" />
                        <span class="text-danger" v-if="errors.has('url' + _uid)">{{ errors.first('url' + _uid) }}</span>
                    </div>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{$t('address.cardTitle')}}</div>
                </div>
                <div class="card-body">
                    <app-address :address="accommodationPlace.address"></app-address>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{$t('contactPerson')}}</div>
                </div>
                <div v-if="showContactPerson">
                    <div class="card-body">
                        <app-person :person="accommodationPlace.contact"></app-person>
                    </div>
                </div>
                <div class="text-center m-1">
                    <div v-if="!showContactPerson">
                        <button type="button" class="btn btn-primary btn-md mr-2"
                                @click="setContactDetails">
                            <i class="fas fa-user-plus"></i>
                            {{ $t('add') }}
                        </button>
                    </div>
                    <div v-else>
                        <button type="button"
                                class="btn btn-primary"
                                @click="setContactDetails">
                            <i class="fas fa-user-minus"></i> {{ $t('remove') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{$t('accommodation.title')}}</div>
                </div>
                <div class="card-body">
                    <app-accommodation :accommodation="accommodationPlace.accommodation"></app-accommodation>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{$t('otherFields.otherFields')}}</div>
                </div>
                <div class="card-body">
                    <otherFields :otherFields="accommodationPlace.otherFields"></otherFields>
                </div>
            </div>

            <AuxiliaryRegister ref="auxiliaryRegisterRef"
                               :register-id="AuxiliaryRegisters.AccommodationPlaces"
                               register-name="AccommodationPlace"
                               :entry-id="accommodationPlace.auxRegisterEntityId"></AuxiliaryRegister>

            <div class="text-center mt-3 mb-5">
                <button type="button" class="btn btn-primary btn" @click="onSubmit">
                    <i class="fas fa-save"></i>
                    {{ $t('buttons.save.btnText') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import AppAddress from '../../../components/address/Address';
    import { AccommodationPlace, Person } from '@/models/models';
    import AppPerson from '../../../components/person/ContactPerson';
    import AppAccommodation from '../../../components/accommodation/Accommodation';
    import Dropdown from '../../../components/dropdown/NomenclatureDropdown'
    import OtherFields from '../../../components/otherFields/OtherFields.vue';
    import PageTitle from '../../../components/common/PageTitle';
    import { AuxiliaryRegisters, NomenclatureParentBySort } from '@/models/enums'
    import AuxiliaryRegister from '@/components/dynamic/AuxiliaryRegister';

    export default {
        inject: ['$validator'],
        components: {
            Dropdown,
            'appAddress': AppAddress,
            'appPerson': AppPerson,
            'appAccommodation': AppAccommodation,
            OtherFields,
            PageTitle,
            AuxiliaryRegister
        },
        data() {
            return {
                nomenclature: NomenclatureParentBySort,
                id: null,
                accommodationPlace: new AccommodationPlace(),
                loading: false,
                showContactPerson: false,
                isDifferentName: false,
                oldName: '',
                isSaved: false,
                AuxiliaryRegisters: AuxiliaryRegisters
            }
        },
        mounted() {
            this.id = this.$route.params.id;
            if (this.id) {
                this.loadData();
            }
            this.$store.initialModel = _.cloneDeep(this.accommodationPlace);
        },
        beforeRouteLeave(to, from, next) {
            if (!_.isEqual(this.$store.initialModel, this.accommodationPlace) && !this.isSaved) {
                const answer = window.confirm(this.$t('notifications.leavePage'))
                if (answer) {
                    next()
                } else {
                    next(false)
                }
            } else {
                next()
            }
        },
        methods: {
            onSubmit() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.accommodationPlace.auxRegisterModel = this.$refs.auxiliaryRegisterRef.getDynamicRegisterModelForSubmit();
                        if (this.id) {
                            this.edit();
                        } else {
                            this.create();
                        }
                    }
                })
            },
            setContactDetails() {
                this.showContactPerson = !this.showContactPerson;
                if (this.showContactPerson) {
                    this.accommodationPlace.contact = new Person();
                } else {
                    this.accommodationPlace.contact = null;
                }
            },
            backToRegister() {
                this.$router.push('/registers/accommodationPlaces');
            },
            create() {
                this.loading = true;
                this.$http.post('/api/accommodationPlace', this.accommodationPlace)
                    .then(result => {
                        this.loading = false;
                        this.isSaved = true;
                        this.$vs.notify({
                            title: this.$t('notifications.success'),
                            text: this.$t('notifications.successfulSavedData'),
                            position: 'top-center',
                            color: 'success'
                        });
                        this.backToRegister();
                    }).catch(error => {
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t('notifications.error'),
                            text: this.$helpers.errorText(error),
                            position: 'top-center',
                            color: 'danger'
                        });
                    });
            },
            edit() {
                this.loading = true;
                this.$http.put('/api/accommodationPlace', this.accommodationPlace)
                    .then(result => {
                        this.loading = false;
                        this.isSaved = true;
                        this.$vs.notify({
                            title: this.$t('notifications.success'), // TODO:
                            text: this.$t('notifications.successfulSavedData'), // TODO:
                            position: 'top-center',
                            color: 'success'
                        });
                        this.backToRegister();
                    }).catch(error => {
                        this.loading = false;
                        this.$vs.notify({
                            title: this.$t('notifications.error'), // TODO:
                            text: this.$helpers.errorText(error), // TODO:
                            position: 'top-center',
                            color: 'danger'
                        });
                    });
            },
            loadData() {
                this.loading = true;
                this.$http.get(`/api/accommodationPlace/${this.id}`).then(result => {
                    this.accommodationPlace = new AccommodationPlace(result.data);
                    this.$store.initialModel = _.cloneDeep(this.accommodationPlace);
                    this.isDifferentName = false;
                    this.oldName = this.accommodationPlace.name;
                    this.loading = false;
                    if (this.accommodationPlace.contact.firstName !== "") {
                        this.showContactPerson = true;
                    }
                }).catch(error => {
                    // TODO: Notify
                    this.loading = false;
                });
            },
        },
        watch: {
            'accommodationPlace.name'(newVal, oldVal) {
                if (newVal === this.oldName) {
                    this.isDifferentName = false;
                } else {
                    this.isDifferentName = true;
                }
            }
        }
    }
</script>

<style scoped>
</style>
